import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import EventIcon from '@mui/icons-material/Event';
import { TimelineItem, TimelineRow, TimelineRowGroup } from 'mq-timeline-calendar/react';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import { useNotify } from '@/stores/notificationStore';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import { useStaffUsers } from '@/hooks/useStaffUsers';
import { useCompleteTask, type TaskListItem } from '@/hooks/useTasks';
import { describeApiError } from '@/lib/describeApiError';
import { staffDisplayName } from '@/utils/staffNames';
import {
  canCompleteTask,
  canRescheduleTask,
  type TaskActor,
} from '@/features/tasks/taskCalendarAccess';
import {
  useRescheduleCalendarTask,
  useSupervisorCalendarTasks,
} from '@/features/tasks/useSupervisorCalendarTasks';
import {
  addDaysToDate,
  assignLanes,
  buildSupervisorTaskEvents,
  calendarLocaleFor,
  formatTenantDate,
  planReschedule,
  resolveTenantTimeZone,
  tenantToday,
  viewWindow,
  type SupervisorAllDayEvent,
  type SupervisorCalendarView,
  type SupervisorTimedEvent,
} from './supervisorTaskCalendar';
import { SupervisorTaskCalendarGrid } from './SupervisorTaskCalendarGrid';
import { SupervisorTaskDialog } from './SupervisorTaskDialog';

const ERROR_CODE_PREFIX = 'calendar:supervisorTasks.errorCode';
const SIDEBAR_WIDTH = 160;

interface SupervisorTaskCalendarTabProps {
  /** Fill the parent's available height (desktop full-bleed layout). */
  fillHeight?: boolean;
}

export function SupervisorTaskCalendarTab({ fillHeight = false }: SupervisorTaskCalendarTabProps) {
  const { t, i18n } = useTranslation('calendar');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const isMobile = useIsMobile();
  const themeMode = useUIStore((s) => s.themeMode);
  const user = useAuthStore((s) => s.user);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canViewOthers = hasPermission('hr.view_team');
  const fill = fillHeight && !isMobile;
  const calendarHeight = fill
    ? '100%'
    : isMobile
      ? 'calc(100vh - 290px)'
      : 'calc(100vh - 370px)';

  const { tenantSettings, isPending: settingsPending } = useTenantSettings();
  const timeZone = resolveTenantTimeZone(tenantSettings?.timezone);

  const [anchorDate, setAnchorDate] = useState<string | null>(null);
  const [view, setView] = useState<SupervisorCalendarView>(() => (isMobile ? 'day' : 'week'));
  const [includeAnyone, setIncludeAnyone] = useState(false);
  const [assignee, setAssignee] = useState('all');
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  // Bumped whenever a drop is refused or ignored so the timeline remounts at
  // the server's positions instead of wherever the bar was released.
  const [timelineKey, setTimelineKey] = useState(0);

  // Today is a tenant date, not the browser's.
  const currentDate = anchorDate ?? tenantToday(new Date(), timeZone);
  const range = useMemo(
    () => viewWindow(currentDate, view, timeZone),
    [currentDate, view, timeZone],
  );

  const staffUsersQuery = useStaffUsers({ isActive: true });
  const staff = useMemo(
    () => (staffUsersQuery.data?.users ?? []).filter((member) => member.id !== user?.id),
    [staffUsersQuery.data?.users, user?.id],
  );

  const { data, isPending, isFetching, isError, error, refetch } = useSupervisorCalendarTasks(
    { from: range.from.toISOString(), to: range.to.toISOString(), assignee },
    { enabled: !settingsPending },
  );
  const rescheduleTask = useRescheduleCalendarTask();
  const completeTask = useCompleteTask();

  const actor: TaskActor = useMemo(
    () => ({ userId: user?.id, role: user?.role, hasPermission }),
    [user?.id, user?.role, hasPermission],
  );

  // Looked up in the live list so the dialog never offers actions on a stale copy.
  const selectedTask = useMemo(
    () => data?.tasks.find((task) => task.id === selectedTaskId) ?? null,
    [data?.tasks, selectedTaskId],
  );

  const events = useMemo(
    () => buildSupervisorTaskEvents(data?.tasks ?? [], { timeZone, includeAnyone }),
    [data?.tasks, timeZone, includeAnyone],
  );
  const timed = useMemo(
    () => assignLanes(events.filter((e): e is SupervisorTimedEvent => e.kind === 'timed')),
    [events],
  );
  const allDay = useMemo(
    () => assignLanes(events.filter((e): e is SupervisorAllDayEvent => e.kind === 'allDay')),
    [events],
  );

  // The grid gets real epoch bounds: the tenant's first midnight up to the next
  // one after the last day (DST days are 23 or 25 hours). It draws its header and
  // lines in the tenant zone and keeps panning inside exactly this fetched window.
  const startDate = range.from.getTime();
  const endDate = range.endExclusive.getTime();

  const timelineLocale = useMemo(() => calendarLocaleFor(i18n.language), [i18n.language]);

  const step = view === 'week' ? 7 : 1;
  const handlePrevious = useCallback(() => {
    setAnchorDate(addDaysToDate(currentDate, -step));
  }, [currentDate, step]);
  const handleNext = useCallback(() => {
    setAnchorDate(addDaysToDate(currentDate, step));
  }, [currentDate, step]);
  const handleToday = useCallback(() => {
    setAnchorDate(null);
  }, []);

  const dateLabel =
    view === 'week'
      ? `${formatTenantDate(range.firstDay, i18n.language, { day: 'numeric', month: 'numeric' })} – ${formatTenantDate(range.lastDay, i18n.language, { day: 'numeric', month: 'numeric', year: 'numeric' })}`
      : formatTenantDate(currentDate, i18n.language, {
          weekday: 'long',
          day: 'numeric',
          month: 'numeric',
          year: 'numeric',
        });

  const handleDrop = useCallback(
    async (event: SupervisorTimedEvent, droppedAt: number) => {
      const plan = planReschedule(event.task, droppedAt, timeZone);
      if (plan.kind === 'noop') {
        setTimelineKey((key) => key + 1);
        return;
      }
      try {
        await rescheduleTask.mutateAsync({
          id: event.task.id,
          scheduledAt: plan.scheduledAt,
          expectedUpdatedAt: plan.expectedUpdatedAt,
        });
        notify.success(t('supervisorTasks.moved'), t('supervisorTasks.movedDesc'));
      } catch (err) {
        // Nothing was written (or someone else's edit won): show the server's state.
        setTimelineKey((key) => key + 1);
        void refetch();
        notify.error(
          t('supervisorTasks.rescheduleFailed'),
          describeApiError(err, tc, { codePrefix: ERROR_CODE_PREFIX, fallback: tc('errors.generic') }),
        );
      }
    },
    [rescheduleTask, timeZone, notify, t, tc, refetch],
  );

  const handleComplete = useCallback(
    async (task: TaskListItem) => {
      try {
        await completeTask.mutateAsync(task.id);
        notify.success(t('supervisorTasks.completed'));
        setSelectedTaskId(null);
      } catch (err) {
        void refetch();
        notify.error(
          t('supervisorTasks.completeFailed'),
          describeApiError(err, tc, { codePrefix: ERROR_CODE_PREFIX, fallback: tc('errors.generic') }),
        );
      }
    },
    [completeTask, notify, t, tc, refetch],
  );

  const barSx = (task: TaskListItem) => ({
    height: '100%',
    px: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    borderRadius: 0.5,
    overflow: 'hidden',
    cursor: 'pointer',
    bgcolor: task.status === 'completed' ? 'action.disabledBackground' : 'primary.main',
    color: task.status === 'completed' ? 'text.disabled' : 'primary.contrastText',
    ...(task.status === 'completed' && { textDecoration: 'line-through' }),
  });

  const rowStyle = {
    borderBottom: `1px solid ${themeMode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
  };
  const sidebarCell = (label: string, lanes: number) => (
    <Box
      sx={{
        height: `calc(var(--timeline-row-height, 60px) * ${String(lanes)})`,
        display: 'flex',
        alignItems: 'center',
        px: 1.5,
        borderBottom: 1,
        borderColor: 'divider',
        boxSizing: 'border-box',
      }}
    >
      <Typography variant="caption" fontWeight={600} noWrap>
        {label}
      </Typography>
    </Box>
  );

  if (isError && !data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void refetch()}>
              {tc('errors.refresh')}
            </Button>
          }
        >
          {t('supervisorTasks.errorLoading')}:{' '}
          {describeApiError(error, tc, { codePrefix: ERROR_CODE_PREFIX, fallback: tc('errors.generic') })}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      className="os-timeline-root"
      sx={fill ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : undefined}
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 2, flexShrink: 0 }}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
      >
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title={tc('previous')}>
            <IconButton onClick={handlePrevious} size="small" aria-label={tc('previous')}>
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={tc('today')}>
            <IconButton onClick={handleToday} size="small" aria-label={tc('today')}>
              <TodayIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={tc('next')}>
            <IconButton onClick={handleNext} size="small" aria-label={tc('next')}>
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        <Typography variant="h6" sx={{ minWidth: 200, textTransform: 'capitalize' }}>
          {dateLabel}
        </Typography>

        <Stack direction="row" spacing={1}>
          {(['day', 'week'] as SupervisorCalendarView[]).map((item) => (
            <Chip
              key={item}
              label={tc(item)}
              onClick={() => { setView(item); }}
              color={view === item ? 'primary' : 'default'}
              variant={view === item ? 'filled' : 'outlined'}
              size="small"
            />
          ))}
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ ml: 'auto !important' }}>
          <TextField
            select
            size="small"
            label={t('supervisorTasks.assignee')}
            value={assignee}
            onChange={(e) => { setAssignee(e.target.value); }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="all">{t('supervisorTasks.assigneeAll')}</MenuItem>
            {user?.id && <MenuItem value={user.id}>{t('supervisorTasks.assigneeMine')}</MenuItem>}
            <MenuItem value="unassigned">{t('supervisorTasks.assigneeUnassigned')}</MenuItem>
            {canViewOthers &&
              staff.map((member) => (
                <MenuItem key={member.id} value={member.id}>
                  {staffDisplayName(member)}
                </MenuItem>
              ))}
          </TextField>
          <FormControlLabel
            control={
              <Switch
                checked={includeAnyone}
                onChange={(e) => { setIncludeAnyone(e.target.checked); }}
              />
            }
            label={t('supervisorTasks.includeAnyone')}
          />
        </Stack>
      </Stack>

      {data?.truncated && (
        <Alert severity="warning" sx={{ mb: 2, flexShrink: 0 }}>
          {t('supervisorTasks.truncated')}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          position: 'relative',
          touchAction: isMobile ? 'pan-y' : 'auto',
          ...(fill && { flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }),
        }}
        aria-busy={isPending || isFetching}
      >
        {(isPending || isFetching) && (
          <LinearProgress
            aria-label={tc('loading')}
            sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2 }}
          />
        )}
        <SupervisorTaskCalendarGrid
          key={timelineKey}
          startMs={startDate}
          endMs={endDate}
          timeZone={timeZone}
          locale={timelineLocale}
          language={i18n.language}
          height={calendarHeight}
          theme={themeMode}
          sidebar={
            isMobile
              ? undefined
              : {
                  width: SIDEBAR_WIDTH,
                  content: (
                    <>
                      {sidebarCell(t('supervisorTasks.allDayRow'), allDay.laneCount)}
                      {sidebarCell(t('supervisorTasks.scheduledRow'), timed.laneCount)}
                    </>
                  ),
                }
          }
        >
          <TimelineRowGroup>
            <TimelineRow
              id="supervisor-all-day"
              label={t('supervisorTasks.allDayRow')}
              rowCount={allDay.laneCount}
              height={isMobile ? 60 : undefined}
              collapsible={false}
              style={rowStyle}
            >
              {allDay.lanes.map(({ event, lane }) => (
                <TimelineItem
                  key={event.id}
                  startTime={event.startMs}
                  endTime={event.endMs}
                  row={lane}
                  draggable={false}
                >
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => { setSelectedTaskId(event.task.id); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') setSelectedTaskId(event.task.id);
                    }}
                    sx={barSx(event.task)}
                  >
                    <EventIcon fontSize="inherit" />
                    <Typography variant="caption" noWrap>
                      {t('supervisorTasks.deadlineBar', { title: event.task.title })}
                    </Typography>
                  </Box>
                </TimelineItem>
              ))}
            </TimelineRow>
            <TimelineRow
              id="supervisor-timed"
              label={t('supervisorTasks.scheduledRow')}
              rowCount={timed.laneCount}
              height={isMobile ? 60 : undefined}
              collapsible={false}
              style={rowStyle}
            >
              {timed.lanes.map(({ event, lane }) => (
                <TimelineItem
                  key={event.id}
                  startTime={event.startMs}
                  endTime={event.endMs}
                  row={lane}
                  draggable={
                    !isMobile && !rescheduleTask.isPending && canRescheduleTask(actor, event.task)
                  }
                  onDragEnd={(droppedAt) => { void handleDrop(event, droppedAt); }}
                >
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => { setSelectedTaskId(event.task.id); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') setSelectedTaskId(event.task.id);
                    }}
                    sx={barSx(event.task)}
                  >
                    <Typography variant="caption" noWrap>
                      {event.task.title}
                    </Typography>
                  </Box>
                </TimelineItem>
              ))}
            </TimelineRow>
          </TimelineRowGroup>
        </SupervisorTaskCalendarGrid>
      </Paper>

      {!isPending && events.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">{t('supervisorTasks.empty')}</Typography>
        </Box>
      )}

      <SupervisorTaskDialog
        task={selectedTask}
        timeZone={timeZone}
        canComplete={selectedTask !== null && canCompleteTask(actor, selectedTask)}
        completing={completeTask.isPending}
        onComplete={(task) => { void handleComplete(task); }}
        onClose={() => { setSelectedTaskId(null); }}
      />
    </Box>
  );
}
